'use client';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { BaseLineUp } from '@/app/components/icons/BaseLineUp';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { Tab } from '@/app/components/shared/PlanDetail/components/Tab';
import { CREDITS_CONFIG_LIST, type IndividualPlanType } from '@/app/components/shared/PlanDetail/constants';
import { CURRENCY, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { PeriodType, ProductList, SubscriptionProductType, SubsPlanType, TabItemType } from '@/app/types';
import { cn, formatAmount, formatPrice, isTeamPlan } from '@/app/utils';
import { type FeatureCategory, type FeatureValue, getComparisonFeatures } from './features';

interface ComparisonTableProps {
  products: ProductList | null;
  tabList: TabItemType[];
  renderSubscriptionButton: (product: SubscriptionProductType) => React.ReactNode;
  period: PeriodType | null;
  setPeriod: (period: PeriodType | null) => void;
}

export const ComparisonTable: FC<ComparisonTableProps> = ({
  products,
  tabList,
  renderSubscriptionButton,
  period,
  setPeriod,
}) => {
  const t = useTranslations();
  const isChinaSite = SITE === 'china';
  const headerRef = useRef<HTMLDivElement>(null);
  const priceComparisonEndRef = useRef<HTMLDivElement>(null);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);

  const { bonusCode } = useStore((state) => ({
    bonusCode: state.bonusCode,
  }));

  // 防抖函数
  const debounce = useCallback(<T extends (...args: unknown[]) => void>(func: T, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  // 滚动处理函数
  const handleScroll = useCallback(() => {
    if (headerRef.current && priceComparisonEndRef.current) {
      const headerTop = headerRef.current.offsetTop;
      const endTop = priceComparisonEndRef.current.offsetTop;
      const scrollTop = window.scrollY;

      // 只在套餐价格对比区域内显示固定头部，提前 100px 结束避免遮挡
      setIsHeaderFixed(scrollTop > headerTop && scrollTop < endTop - 180);
    }
  }, []);

  // 防抖的滚动处理函数
  const debouncedHandleScroll = useMemo(() => debounce(handleScroll, 10), [debounce, handleScroll]);

  // 监听滚动事件
  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [debouncedHandleScroll]);

  // 使用新的功能分类数据
  const [categories, setCategories] = useState<FeatureCategory[]>(() =>
    // @ts-expect-error - 跳过翻译函数类型验证
    getComparisonFeatures(t, isChinaSite),
  );

  // 切换分类展开/收起状态
  const toggleCategory = (index: number) => {
    setCategories((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isOpen: !updated[index].isOpen };
      return updated;
    });
  };

  // 渲染功能标记
  const renderFeatureValue = (value: FeatureValue) => {
    if (typeof value === 'boolean') {
      return value ? (
        <BaseLineCheck2 className="text-[#A699FF] text-base" />
      ) : (
        <BaseLineClose className="text-[#C66D44] text-base" />
      );
    }

    return <div className="whitespace-pre-line text-center text-sm text-system-text01">{value}</div>;
  };

  // 渲染表格头部
  const renderTableHeader = () => {
    if (!period || !products || !products[period]) return null;

    return (
      <div className="grid grid-cols-5 gap-x-2 gap-y-0 whitespace-nowrap rounded-16 bg-[#FFFFFF1A] py-4">
        <div className="flex items-center justify-center">
          <div className="whitespace-pre-wrap text-center font-medium text-2xl text-system-white md:text-[10px]">
            {t('pricing.testimonials.comparisonTable.comparePlans')}
          </div>
        </div>
        {Object.keys(products[period] || {}).map((key) => {
          const product = products[period]?.[key as keyof (typeof products)[typeof period]];
          if (!product || isTeamPlan(product.plan_type)) return null;

          const typedPlanType = key as SubsPlanType;

          return (
            <div key={key} className="border-system-white12 border-l px-4 py-4 text-center md:px-2 md:py-2">
              {/* 套餐图标和名称 */}
              <div
                className={cn(
                  'flex items-center gap-2 border-system-white12 font-semibold text-xl lg:text-lg',
                  'md:flex-col md:gap-1',
                  typedPlanType === 'free' ? 'md:h-12 md:justify-center' : 'md:justify-start',
                )}
              >
                <span className="md:text-xs">{t(`payments.${typedPlanType}`)}</span>
              </div>

              {/* 价格 */}
              <div className="mb-2 py-2 text-left text-sm md:hidden">
                <div className="-mr-2 flex items-baseline gap-1 leading-[1.4]">
                  <span className="font-bold text-system-white36 text-xl">{CURRENCY}</span>
                  <span className="font-semibold text-[40px] text-system-white">
                    {formatAmount(product.price / (product.billing_period === 'yearly' ? 12 : 1))}
                  </span>
                  <span className="font-medium text-system-white24">/</span>
                  <span className="text-system-white48">{t('payments.per_monthly')}</span>
                  {/* 中国站 & 月包 & 订阅 & ultimate_v2 */}
                  {SITE === 'china' &&
                  product.billing_period === 'monthly' &&
                  product.product_type === 'subscription' &&
                  product.plan_type === 'ultimate_v2' ? (
                    <span className="text-system-white48 line-through">¥629/月</span>
                  ) : product.original_price && product.discount > 0 ? (
                    <span className="text-system-white48 line-through">{formatPrice(product.original_price)}</span>
                  ) : null}
                </div>
                <div className="h-5 text-sm text-system-white48">
                  {key !== 'free' && product.billing_period !== 'monthly' && (
                    <>
                      {t('payments.plans.billYearlyAs')}
                      {formatPrice(product.price)}
                    </>
                  )}
                </div>
              </div>

              {/* 订阅按钮 */}
              <div className="md:hidden">{renderSubscriptionButton(product)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!products) return null;

  return (
    <div className="my-16 flex w-full flex-col justify-center">
      <div className="text-center">
        <div className="mx-auto inline-block text-center font-semibold text-[32px] text-system-white leading-[32px] sm:text-left sm:text-2xl">
          {t('pricing.testimonials.comparisonTable.title')}
        </div>

        {/* 复用的 Tab 组件 */}
        {tabList.length > 0 && period && (
          <div className="md:hidden">
            <Tab setPeriod={setPeriod} period={period} tabList={tabList} />
          </div>
        )}
      </div>

      {/* 套餐价格对比 */}
      {period && products[period] && (
        <div className="mt-8">
          {/* 固定头部 - 当滚动时显示 */}
          {isHeaderFixed && (
            <div className="fixed top-0 right-0 left-0 z-50 bg-black py-4">
              <div className="mx-auto w-full max-w-[1364px]">
                <div className="mb-6 overflow-x-auto">{renderTableHeader()}</div>
              </div>
            </div>
          )}

          {/* 原始表格头部 */}
          <div ref={headerRef} className="mb-6 overflow-x-auto">
            {renderTableHeader()}
          </div>
        </div>
      )}

      {/* Credits 对比 */}
      {period && products[period] && (
        <div className="mt-8 overflow-x-auto rounded-16 bg-[#FFFFFF1A] py-6 md:py-3">
          {/* Credits 表格头部 */}
          <div className="grid grid-cols-5 gap-x-4 md:gap-x-2">
            <div className="flex items-center justify-center text-left">
              <div className="font-medium text-system-white text-xl md:text-[10px]">
                {t('pricing.testimonials.comparisonTable.credits')}
              </div>
            </div>
            {Object.keys(products[period] || {}).map((key) => {
              const product = products[period]?.[key as keyof (typeof products)[typeof period]];
              if (!product || isTeamPlan(product.plan_type)) return null;

              const { extra_grant_credit = 0 } = product || {};
              // 上方 isTeamPlan 已过滤 team_v2，剩余只可能是个人版
              const typedPlanType = key as IndividualPlanType;
              const creditsConfig = CREDITS_CONFIG_LIST[typedPlanType];

              return (
                <div
                  key={key}
                  className="flex flex-col items-center justify-center border-system-white12 border-l px-6 py-3 text-center md:px-2 md:py-2"
                >
                  {/* Credits 内容 */}
                  {typedPlanType !== 'free' && (
                    <div className="flex flex-col items-center justify-center gap-1">
                      <div className="flex gap-1 whitespace-nowrap text-sm text-system-white md:flex-col md:text-xs">
                        {t.rich('payments.plans.creditsProvide', {
                          credits: () => (
                            <span className={`${creditsConfig.bgImg} bg-clip-text text-transparent`}>
                              {creditsConfig.credits}
                            </span>
                          ),
                        })}
                      </div>
                      {extra_grant_credit > 0 && bonusCode && (
                        <div className="flex items-center gap-1 text-sm text-system-white">
                          达人码专属
                          <span className={`${creditsConfig.bgImg} bg-clip-text text-transparent`}>
                            {extra_grant_credit}
                          </span>
                          积分
                        </div>
                      )}
                      <div className="text-center text-xs md:text-[10px]">
                        {typedPlanType === 'ultimate_v2' ? (
                          <div className="flex items-center justify-center gap-1">
                            <StarIcon className="size-[14px] flex-shrink-0 text-[#9B93FF] md:hidden" />
                            <span className="bg-[linear-gradient(90deg,#D7DAFF_0%,#8E96FF_86.51%)] bg-clip-text text-transparent">
                              {t('payments.plans.hightPriority')}
                            </span>
                          </div>
                        ) : (
                          <div className="flex gap-1 text-system-white64 md:flex-col">
                            {t.rich('payments.plans.creditsGenerate', {
                              videos: () => (
                                <span className={`${creditsConfig.bgImg} bg-clip-text text-transparent`}>
                                  {creditsConfig.videos}
                                </span>
                              ),
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Free 套餐的显示 */}
                  {typedPlanType === 'free' && (
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-center text-sm text-system-white md:text-xs">
                        {t('pricing.testimonials.comparisonTable.features.creditBonus')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 详细功能对比表格 */}
      <div className="w-full overflow-x-auto">
        {/* 功能分类 */}
        {categories.map((category, categoryIndex) => (
          <div key={category.id} className="font-ShengShu">
            {/* 分类标题行 - 可折叠 */}
            <div
              className="flex w-full cursor-pointer items-center justify-between px-4 py-6 hover:bg-system-white04"
              onClick={() => toggleCategory(categoryIndex)}
            >
              <span className="font-medium text-system-text01 text-xl">{category.title}</span>
              {category.isOpen ? (
                <BaseLineUp className="text-system-white64 text-xl" />
              ) : (
                <BaseLineDown className="text-system-white64 text-xl" />
              )}
            </div>

            {/* 分类内的功能列表 */}
            {category.isOpen &&
              category.features.map((feature, featureIndex) => (
                <div key={feature.id}>
                  {/* 旧的桌面端：5列布局 */}
                  <div
                    className={cn(
                      'grid grid-cols-5 border-system-white04 border-t shadow-[0px_1px_0px_0px_rgba(255,255,255,0.25)_inset] md:hidden',
                      featureIndex % 2 === 0 ? 'bg-[#1A1A1A]' : 'bg-black',
                      feature.id === category.features[category.features.length - 1].id && 'border-b',
                    )}
                  >
                    <div className="flex items-center px-4 py-3 font-medium text-sm text-system-text01">
                      {feature.name}
                    </div>
                    <div className="flex items-center justify-center px-4 py-3 font-medium text-sm text-system-text01">
                      {renderFeatureValue(feature.free)}
                    </div>
                    <div className="flex items-center justify-center px-4 py-3 font-medium text-sm text-system-text01">
                      {renderFeatureValue(feature.standard)}
                    </div>
                    <div className="flex items-center justify-center px-4 py-3 font-medium text-sm text-system-text01">
                      {renderFeatureValue(feature.premium)}
                    </div>
                    <div className="flex items-center justify-center px-4 py-3 font-medium text-sm text-system-text01">
                      {renderFeatureValue(feature.ultimate)}
                    </div>
                  </div>

                  {/* 新的移动端：两行布局 */}
                  <div className="hidden md:block">
                    {/* 第一行：功能名称 */}
                    <div className="border-system-white04 border-t bg-[#1A1A1A] px-4 py-3 font-medium text-sm text-system-text01 shadow-[0px_1px_0px_0px_rgba(255,255,255,0.25)_inset]">
                      {feature.name}
                    </div>
                    {/* 第二行：4个套餐值 */}
                    <div
                      className={cn(
                        'grid grid-cols-4 border-system-white04 border-t bg-[#1A1A1A] px-4 py-3 shadow-[0px_1px_0px_0px_rgba(255,255,255,0.25)_inset]',
                        feature.id === category.features[category.features.length - 1].id && 'border-b',
                      )}
                    >
                      <div className="flex items-center justify-center font-medium text-sm text-system-text01">
                        {renderFeatureValue(feature.free)}
                      </div>
                      <div className="flex items-center justify-center font-medium text-sm text-system-text01">
                        {renderFeatureValue(feature.standard)}
                      </div>
                      <div className="flex items-center justify-center font-medium text-sm text-system-text01">
                        {renderFeatureValue(feature.premium)}
                      </div>
                      <div className="flex items-center justify-center font-medium text-sm text-system-text01">
                        {renderFeatureValue(feature.ultimate)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ))}
        {/* 套餐价格对比区域结束标记 */}
        <div ref={priceComparisonEndRef} className="h-0" />
      </div>
    </div>
  );
};
